import type { SortDirection } from '@features/allocation';
import { allFiltersCleared, hasAnyFilter, SORT_DIRECTIONS, textOrNull } from '@features/allocation';
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';
import type { ProjectFilters, ProjectSortField } from '../types/ProjectList';

const SORT_FIELDS = [
  'unit',
  'contract',
  'axisCode',
  'name',
  'status',
  'startDate',
  'endDate',
  'projectManager',
  'operationManager',
  'accountDirector',
  'internal',
  'pool',
] as const satisfies readonly ProjectSortField[];

const DEFAULT_PER_PAGE = 20;

const PARSERS = {
  axisCode: parseAsString,
  name: parseAsString,
  clientSearch: parseAsString,
  contractSearch: parseAsString,
  unitId: parseAsInteger,
  statusIds: parseAsArrayOf(parseAsInteger),
  projectManagerSearch: parseAsString,
  operationManagerSearch: parseAsString,
  accountDirectorSearch: parseAsString,
  internal: parseAsBoolean,
  pool: parseAsBoolean,
  sortBy: parseAsStringLiteral(SORT_FIELDS),
  sortDirection: parseAsStringLiteral(SORT_DIRECTIONS),
  page: parseAsInteger.withDefault(1),
  perPage: parseAsInteger.withDefault(DEFAULT_PER_PAGE),
};

export type ProjectSearchKey =
  | 'axisCode'
  | 'name'
  | 'clientSearch'
  | 'contractSearch'
  | 'projectManagerSearch'
  | 'operationManagerSearch'
  | 'accountDirectorSearch';
/** Rascunho do painel "Filtros avançados", comitado de uma vez no "Aplicar". */
export type ProjectAdvancedValues = {
  unitId: number | null;
  axisCode: string;
  name: string;
  contractSearch: string;
  internal: boolean | null;
  pool: boolean | null;
};

type ProjectFlagKey = 'internal' | 'pool';
export type ProjectFilterKey = ProjectSearchKey | ProjectFlagKey | 'unitId' | 'statusIds';

const CLEARED_BY_KEY = {
  axisCode: { axisCode: null },
  name: { name: null },
  clientSearch: { clientSearch: null },
  contractSearch: { contractSearch: null },
  unitId: { unitId: null },
  statusIds: { statusIds: null },
  projectManagerSearch: { projectManagerSearch: null },
  operationManagerSearch: { operationManagerSearch: null },
  accountDirectorSearch: { accountDirectorSearch: null },
  internal: { internal: null },
  pool: { pool: null },
} as const satisfies Record<ProjectFilterKey, object>;

const ALL_FILTERS_CLEARED = allFiltersCleared(CLEARED_BY_KEY);

/** Filtros, ordenação e página na URL, no molde de `useAllocationTagFilters`; filtro novo volta à página 1. */
export function useProjectFilters() {
  const [state, setState] = useQueryStates(PARSERS, { throttleMs: 300 });

  const filters: ProjectFilters = {
    axisCode: state.axisCode ?? undefined,
    name: state.name ?? undefined,
    clientSearch: state.clientSearch ?? undefined,
    contractSearch: state.contractSearch ?? undefined,
    unitId: state.unitId ?? undefined,
    statusIds: state.statusIds ?? undefined,
    projectManagerSearch: state.projectManagerSearch ?? undefined,
    operationManagerSearch: state.operationManagerSearch ?? undefined,
    accountDirectorSearch: state.accountDirectorSearch ?? undefined,
    internal: state.internal ?? undefined,
    pool: state.pool ?? undefined,
    sortBy: state.sortBy ?? undefined,
    sortDirection: state.sortBy ? (state.sortDirection ?? 'asc') : undefined,
    page: state.page,
    perPage: state.perPage !== DEFAULT_PER_PAGE ? state.perPage : undefined,
  };

  return {
    filters,
    hasActiveFilters: hasAnyFilter(CLEARED_BY_KEY, state),
    setSearch: (key: ProjectSearchKey, value: string) =>
      setState({ [key]: textOrNull(value), page: 1 }),
    applyAdvanced: (values: ProjectAdvancedValues) =>
      setState({
        unitId: values.unitId,
        axisCode: textOrNull(values.axisCode),
        name: textOrNull(values.name),
        contractSearch: textOrNull(values.contractSearch),
        internal: values.internal,
        pool: values.pool,
        page: 1,
      }),
    setUnit: (unitId: number | null) => setState({ unitId, page: 1 }),
    setStatuses: (ids: number[]) => setState({ statusIds: ids.length > 0 ? ids : null, page: 1 }),
    setFlag: (key: ProjectFlagKey, value: boolean | null) => setState({ [key]: value, page: 1 }),
    /** `'none'` volta à ordem do legado. */
    setSort: (field: ProjectSortField, direction: SortDirection | 'none') =>
      setState(
        direction === 'none'
          ? { sortBy: null, sortDirection: null }
          : { sortBy: field, sortDirection: direction },
      ),
    clearFilter: (key: ProjectFilterKey) => setState({ ...CLEARED_BY_KEY[key], page: 1 }),
    /** Limpa os filtros, não a ordenação. */
    clearFilters: () => setState({ ...ALL_FILTERS_CLEARED, page: 1 }),
    perPage: state.perPage,
    setPerPage: (perPage: number) => setState({ perPage, page: 1 }),
    goToPage: (page: number) => setState({ page }),
  };
}

export type ProjectFilterState = ReturnType<typeof useProjectFilters>;
